import { useEffect, useState } from 'react';
import { publicClientConfig } from '@/config/public-client-config';
import { BrandGroupEntity } from '@/entities/brand-group.entity';
import { ProductGroupEntity } from '@/entities/product-group.entity';
import { ReviewEntity } from '@/entities/review.entity';
import { useAuth } from '@/context/auth.context';
import { useSocketIo } from '@/context/socket-io.context';
import { useReviewsPageProviderSelectionConnection } from '@/context/use-reviews-page-provider-selection-connection';
import { useReviewsPageProviderTransportConnection } from '@/context/use-reviews-page-provider-transport-connection';
import { Sort } from '@/types/sort.type';

type UseReviewsPageProviderConnectionsArgs = {
    selectionSessionId: string;
    pickedProducts: string[];
    pickedBrandGroups: string[];
    products: ProductGroupEntity[];
    brandGroups: BrandGroupEntity[];
    reviewsOnPage: number;
    filtersQuery: string[];
    sort: Sort;
    selectionReconcileRevision: number;
    setSelectionSessionId: (value: string) => void;
    setPickedReviews: (value: string[]) => void;
    setPickedProducts: (value: string[]) => void;
    setPickedBrandGroups: (value: string[]) => void;
    setProductsState: (value: ProductGroupEntity[]) => void;
    setBrandGroupsState: (value: BrandGroupEntity[]) => void;
    setReviewsAndTotalState: (reviews: ReviewEntity[], total: number) => void;
    setReviewsOnPageState: (value: number) => void;
    onReviewAnswerJobDone: (review: ReviewEntity) => void;
    onReviewFeedItemChanged: (review: ReviewEntity) => void;
};

export const useReviewsPageProviderConnections = ({
    selectionSessionId,
    pickedProducts,
    pickedBrandGroups,
    products,
    brandGroups,
    reviewsOnPage,
    filtersQuery,
    sort,
    selectionReconcileRevision,
    setSelectionSessionId,
    setPickedReviews,
    setPickedProducts,
    setPickedBrandGroups,
    setProductsState,
    setBrandGroupsState,
    setReviewsAndTotalState,
    setReviewsOnPageState,
    onReviewAnswerJobDone,
    onReviewFeedItemChanged,
}: UseReviewsPageProviderConnectionsArgs) => {
    const { events } = useSocketIo();
    const { accessToken, clientId } = useAuth();
    const [isReviewsDataReconciliationSelectionVisualRunning, setIsReviewsDataReconciliationSelectionVisualRunning] =
        useState(false);

    const {
        enqueueReviewSelectionMutation,
        waitForReviewSelectionReconcile,
        isReviewSelectionReconcileUiRunning,
        isReviewSelectionMutationRunning,
    } = useReviewsPageProviderSelectionConnection({
        accessToken,
        selectionSessionId,
        filtersQuery,
        sort,
        selectionReconcileRevision,
        setPickedReviews,
    });

    const {
        getProducts,
        getBrandGroups,
        refetchReviews,
        refetchReviewsAndProducts,
        setReviewsOnPage,
        isReviewsRefetchRunning,
    } = useReviewsPageProviderTransportConnection({
        accessToken,
        clientId,
        events,
        selectionSessionId,
        pickedProducts,
        pickedBrandGroups,
        products,
        brandGroups,
        reviewsOnPage,
        selectionReconcileRevision,
        filtersQuery,
        sort,
        setSelectionSessionId,
        setPickedProducts,
        setPickedBrandGroups,
        setProductsState,
        setBrandGroupsState,
        setReviewsAndTotalState,
        setReviewsOnPageState,
        onReviewAnswerJobDone,
        onReviewFeedItemChanged,
    });

    const isReviewsDataReconciliationRunning =
        isReviewsRefetchRunning || isReviewSelectionReconcileUiRunning || isReviewSelectionMutationRunning;
    const reviewsDataReconciliationSelectionVisualDelayMs =
        publicClientConfig.reviewsDataReconciliationSelectionVisualDelayMs;

    useEffect(() => {
        if (!isReviewsDataReconciliationRunning) {
            setIsReviewsDataReconciliationSelectionVisualRunning(false);
            return;
        }

        if (reviewsDataReconciliationSelectionVisualDelayMs === 0) {
            setIsReviewsDataReconciliationSelectionVisualRunning(true);
            return;
        }

        const timerId = setTimeout(() => {
            setIsReviewsDataReconciliationSelectionVisualRunning(true);
        }, reviewsDataReconciliationSelectionVisualDelayMs);

        return () => {
            clearTimeout(timerId);
        };
    }, [isReviewsDataReconciliationRunning, reviewsDataReconciliationSelectionVisualDelayMs]);

    return {
        enqueueReviewSelectionMutation,
        waitForReviewSelectionReconcile,
        getProducts,
        getBrandGroups,
        refetchReviews,
        refetchReviewsAndProducts,
        setReviewsOnPage,
        isReviewsDataReconciliationRunning,
        isReviewsDataReconciliationSelectionVisualRunning,
    };
};
